// LED discovery for the setup wizard: finds which GPIO the user's LED is on by
// bisection, then its polarity. Logic only; the dialog is in ledwizard_ui.js.
//
// The user has none, one or two LEDs. With one it shows everything (print activity and the Wi-Fi states). With two
// the first is the print LED (found first) and the second the Wi-Fi LED: the same search runs twice, the second time
// without the pin of the first.
//
// Every round splits the remaining candidate pins in two halves. The pins of
// half A BLINK (driven high and low alternately), the pins of half B are floating
// inputs. An LED on an A pin blinks whether it lights on high or on low, and an
// LED on a B pin stays dark, so the user answers one polarity independent
// question: "is your LED blinking?". 29 pins take 5 rounds. After the last round
// one more check blinks only the found pin (this catches a wrong answer, an LED
// that is always on, or an LED on none of the pins), then the pin is held high
// and the user says whether the LED lights (active high) or not (active low).

import { LED_PINS, PIN_MODE } from './device.js';

export class LedSearch {
  constructor(candidates = LED_PINS) {
    this.remaining = [...candidates];
    this.round = 0;
  }

  get done() {
    return this.remaining.length === 1;
  }

  get pin() {
    return this.done ? this.remaining[0] : null;
  }

  // Number of rounds this search needs at most.
  static roundsFor(n) {
    return Math.ceil(Math.log2(n));
  }

  // The pins that blink in the current round.
  get blink() {
    return this.remaining.slice(0, Math.ceil(this.remaining.length / 2));
  }

  answer(blinking) {
    const half = Math.ceil(this.remaining.length / 2);
    this.remaining = blinking ? this.remaining.slice(0, half) : this.remaining.slice(half);
    this.round++;
  }
}

// step: 'round' | 'verify' | 'polarity' | 'nextled' | 'ready' | 'saving' | 'failed'
// count: 1 or 2 LEDs. slot: 0 = the first (print) LED, 1 = the second (Wi-Fi) LED. leds: what is found so far.
export class LedWizard {
  constructor(device, { candidates = LED_PINS, count = 1, onChange = () => {} } = {}) {
    this.device = device;
    this.candidates = candidates;
    this.count = count;
    this.onChange = onChange;
    this.step = 'idle';
    this.search = null;
    this.slot = 0;
    this.leds = []; // [{pin, activeLow}] of the slots that are done
    this.pin = null; // the pin of the LED being worked on (or the last one)
    this.ledActiveLow = null;
    this.error = null;
    this.testing = false; // a pin test is running on the ESP
    this.searchStart = candidates.length;
  }

  get totalRounds() {
    return LedSearch.roundsFor(this.searchStart);
  }

  // Which LED is being set up: null with a single LED ("the LED"), else 'print' or 'Wi-Fi'.
  get ledName() {
    return this.count === 1 ? null : this.slot === 0 ? 'print' : 'Wi-Fi';
  }

  get roundNumber() {
    return (this.search?.round ?? 0) + 1;
  }

  get remainingCount() {
    return this.search?.remaining.length ?? this.candidates.length;
  }

  // The pins a search for the current slot may use: not the ones already found for this device.
  get slotCandidates() {
    return this.candidates.filter((p) => !this.leds.some((l) => l.pin === p));
  }

  #set(step, error = null) {
    this.step = step;
    this.error = error;
    this.onChange(this);
  }

  async #drive(mode, pins) {
    const status = await this.device.pinTest(mode, pins);
    if (status !== 0) throw new Error(`the device refused the pin test (status ${status})`);
    this.testing = true;
  }

  // Call after the user confirmed that the serial cable is unplugged.
  async start() {
    this.slot = 0;
    this.leds = [];
    await this.#startSlot();
  }

  async #startSlot() {
    this.search = new LedSearch(this.slotCandidates);
    this.searchStart = this.search.remaining.length;
    this.pin = this.ledActiveLow = null;
    await this.#showRound();
  }

  async #showRound() {
    this.#set('round');
    await this.#drive(PIN_MODE.BLINK, this.search.blink);
  }

  // kind: 'yes' | 'no' | 'unsure' (the user could not tell: the same test is shown again)
  async answer(kind) {
    try {
      if (this.step === 'round') {
        if (kind === 'unsure') return await this.#drive(PIN_MODE.BLINK, this.search.blink);
        this.search.answer(kind === 'yes');
        if (!this.search.done) return await this.#showRound();
        this.pin = this.search.pin;
        this.#set('verify');
        return await this.#drive(PIN_MODE.BLINK, [this.pin]);
      }
      if (this.step === 'verify') {
        if (kind === 'unsure') return await this.#drive(PIN_MODE.BLINK, [this.pin]);
        if (kind === 'no') {
          this.pin = null;
          return this.#set('failed', 'The LED does not blink on the pin that was found. Either an answer was wrong, the LED is always on or off, or it is not on a pin this wizard can test.');
        }
        this.#set('polarity');
        return await this.#drive(PIN_MODE.HIGH, [this.pin]);
      }
      if (this.step === 'polarity') {
        if (kind === 'unsure') return await this.#drive(PIN_MODE.HIGH, [this.pin]);
        this.ledActiveLow = kind === 'no'; // pin held high: LED on = active high, dark = active low
        this.leds.push({ pin: this.pin, activeLow: this.ledActiveLow });
        if (this.leds.length < this.count) {
          this.#set('nextled');
          return await this.#drive(PIN_MODE.BLINK, []); // everything dark while the user reads
        }
        return this.#set('ready');
      }
      if (this.step === 'nextled') {
        this.slot++;
        return await this.#startSlot();
      }
    } catch (e) {
      this.#set('failed', e.message);
    }
  }

  // Starts over from the first round (after a failure); the test keeps running.
  async retry() {
    try {
      await this.start();
    } catch (e) {
      this.#set('failed', e.message);
    }
  }

  // The user chose "next" after the first LED was found: starts the search for the second one.
  async next() {
    return this.answer('next');
  }

  // Stores the LED pins and polarities, keeping the other pins as they are. The ESP
  // restarts after this, so the serial port has to be reopened by the caller.
  async save() {
    if (this.step !== 'ready') throw new Error('nothing to save yet');
    const [a, b] = this.leds;
    return this.#save(a.pin, a.activeLow, b?.pin ?? null, b?.activeLow ?? false);
  }

  // "I have no LED": the step counts as done with no pin.
  async saveNoLed() {
    return this.#save(null, false, null, false);
  }

  async #save(led, ledActiveLow, led2, led2ActiveLow) {
    this.#set('saving');
    try {
      const current = await this.device.getConfig();
      if (!current) throw new Error('could not read the current configuration');
      const status = await this.device.setConfig({ ...current.config, led, ledActiveLow, led2, led2ActiveLow, ledDone: true });
      if (status !== 0) throw new Error(`the device refused the configuration (status ${status})`);
      this.testing = false; // the restart ends the test
    } catch (e) {
      this.#set('failed', e.message);
      throw e;
    }
  }

  // Leaves the wizard without saving: ends a running test (this restarts the ESP).
  async cancel() {
    if (!this.testing) return;
    this.testing = false;
    try {
      await this.device.pinTest(PIN_MODE.STOP);
    } catch {
      // the ESP restarts and may not answer
    }
  }
}
